import React from "react";
import { Modal, message } from "antd";
import orderApi from "@/services/api/order";
import Address from "../Address";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import { getOriginalConsigneeAddress, hasLocalConsigneeInfo, isConsigneeExist } from "@/utils/OrderUtils";
import { omit } from "@/utils";
import ShopType from "@/enum/ShopType";
import { SupplierShopTypes } from "@/utils/ShopUtils";
import { cloneDeep } from "lodash";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import ConsigneeDecryptButton from "./ConsigneeDecryptButton";
import CopyButton from "@/components/CopyButton";
import UpdateConsigneeButton from "./UpdateConsigneeButton";

interface ConsigneeProps {
    order: OrderDTO;
    shop: ShopDTO;
    onUpdateSuccess?(): void;
    onOrderUpdate?(orderId: number, order: Partial<OrderDTO>): void;
}

const Consignee: React.FC<ConsigneeProps> = (props) => {
    const { order, shop, onUpdateSuccess, onOrderUpdate } = props;
    const orderManagementStore = useOrderManagementStore();
    const hasLocalAddress = hasLocalConsigneeInfo(order.consignee);
    const shopType = shop.type;

    function handleDeleteLocalAddress() {
        const shopId = order.shopId;

        const address = getOriginalConsigneeAddress(order);

        Modal.confirm({
            content: `确定要使用原地址:${address} 吗?`,
            async onOk() {
                try {
                    const { provinceCode, cityCode, districtCode } = await orderApi.deleteLocalConsignee({
                        orderId: order.id,
                        shopId,
                    });

                    const newOrder = cloneDeep(order);
                    newOrder.provinceCode = provinceCode;
                    newOrder.cityCode = cityCode;
                    newOrder.districtCode = districtCode;
                    const consigneeKey = Object.keys(newOrder.consignee);
                    consigneeKey.forEach((val) => {
                        if (val.indexOf("local") > -1) {
                            newOrder.consignee = omit(newOrder.consignee, [val]);
                        }
                    });

                    orderManagementStore?.table?.updateOrder(order.id, newOrder);

                    onUpdateSuccess?.();

                    onOrderUpdate?.(order.id, newOrder);

                    message.success("恢复使用原地址成功");
                } catch (e) {
                    console.error(e);
                    message.error("恢复使用原地址失败,请刷新页面后再试");
                }
            },
        });
    }

    /** 收货人信息为空不展示操作按钮 */
    const showOperationBtn = isConsigneeExist(order.consignee);

    return (
        <div className="mb3">
            <div>
                <Address
                    id={`address-${order.id}`}
                    order={order}
                    shopType={shopType}
                    useLocalAddress={!!hasLocalAddress}
                    onOrderUpdate={onOrderUpdate}
                />
                <>
                    {showOperationBtn ? (
                        <>
                            {![ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(shopType) ? (
                                <ConsigneeDecryptButton order={order} onOrderUpdate={onOrderUpdate} />
                            ) : null}
                            {![...SupplierShopTypes, ShopType.PDD_FDS].includes(shop.type) ? (
                                <CopyButton
                                    className="ml5 small text-light link-btn"
                                    getElement={() => document.getElementById(`address-${order.id}`)}
                                    filterText="虚拟号"
                                />
                            ) : null}
                            {![ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(shopType) ? (
                                <UpdateConsigneeButton
                                    order={order}
                                    onUpdateSuccess={onUpdateSuccess}
                                    className="ml5 small text-light link-btn"
                                />
                            ) : null}
                        </>
                    ) : null}
                </>
            </div>

            {hasLocalAddress ? (
                <p className="text-muted small">
                    原地址:
                    <Address order={order} shopType={shopType} replayMask={true} onOrderUpdate={onOrderUpdate} />
                    <a
                        role="button"
                        tabIndex={-1}
                        className="text-muted link-btn"
                        style={{ userSelect: "none" }}
                        onClick={handleDeleteLocalAddress}
                    >
                        [使用原地址]
                    </a>
                </p>
            ) : null}
        </div>
    );
};

export default observer(Consignee);
